import type * as React from 'react';
import { Menu } from '@base-ui/react/menu';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

export const RailMenu = Menu.Root;

export interface RailMenuTriggerProps extends Omit<
  Menu.Trigger.Props,
  'className' | 'style'
> {
  className?: string;
}

/*
 * The account avatar. It is the trigger itself rather than a wrapper around one,
 * so the popup anchors to the circle and not to a larger hit area.
 *
 * The circle is all this draws: what fills it is an `Avatar`, so the disc the
 * user sees of themselves here is the same disc everybody else's is drawn as —
 * their picture, or their initials in their own tone. Hence the clipping and the
 * absence of any colour of its own.
 */
export function RailMenuTrigger({ className, ...props }: RailMenuTriggerProps) {
  return (
    <Menu.Trigger
      data-slot='rail-menu-trigger'
      className={cn(
        'flex size-9.5 cursor-pointer items-center justify-center overflow-hidden rounded-full',
        'transition hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        className,
      )}
      {...props}
    />
  );
}

export interface RailMenuContentProps extends Omit<
  Menu.Popup.Props,
  'className' | 'style'
> {
  className?: string;
  /* `bottom` for the same account menu in a phone's top bar. */
  side?: Menu.Positioner.Props['side'];
}

/*
 * Opens to the right of the rail and bottom-aligned — it sits at the very
 * bottom of the viewport. From a top bar it drops down, flush with the right.
 */
export function RailMenuContent({
  className,
  children,
  side = 'right',
  ...props
}: RailMenuContentProps) {
  return (
    <Menu.Portal>
      <Menu.Positioner side={side} align='end' sideOffset={12} className='z-50'>
        <Menu.Popup
          data-slot='rail-menu-content'
          className={cn(
            'min-w-43 rounded-md border border-line bg-surface p-1.5 shadow-menu outline-none animate-pop',
            className,
          )}
          {...props}
        >
          {children}
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  );
}

export const railMenuItemVariants = cva(
  [
    'flex w-full cursor-pointer items-center gap-2.5 rounded-sm px-2.75 py-2.25 text-left',
    'text-sm font-semibold transition-colors outline-none',
    '[&_svg]:size-3.75 [&_svg]:shrink-0',
  ],
  {
    variants: {
      tone: {
        default: 'text-ink [&_svg]:text-ink-muted hover:bg-fill data-highlighted:bg-fill',
        danger:
          'text-danger [&_svg]:text-danger hover:bg-danger-fill data-highlighted:bg-danger-fill',
      },
    },
    defaultVariants: {
      tone: 'default',
    },
  },
);

export type RailMenuItemVariants = VariantProps<typeof railMenuItemVariants>;

export interface RailMenuItemProps
  extends Omit<Menu.Item.Props, 'className' | 'style'>, RailMenuItemVariants {
  className?: string;
}

export function RailMenuItem({ className, tone, ...props }: RailMenuItemProps) {
  return (
    <Menu.Item
      data-slot='rail-menu-item'
      className={cn(railMenuItemVariants({ tone }), className)}
      {...props}
    />
  );
}

export type RailMenuSeparatorProps = Omit<
  React.ComponentProps<typeof Menu.Separator>,
  'style'
>;

export function RailMenuSeparator({ className, ...props }: RailMenuSeparatorProps) {
  return (
    <Menu.Separator
      data-slot='rail-menu-separator'
      className={cn('mx-1 my-1.25 h-px bg-fill', className)}
      {...props}
    />
  );
}
